import { getUser } from './views';
import { ownerBadgeHTML, isOwner } from './ownerBadge.js';
import Pusher from 'pusher-js';
// MultiplayerService imports
import {
    connectToPeer,
    getMyPeerId,
    getActiveConnections,
    sendMessageToPeer // For sending direct P2P greetings later, if needed
} from '../services/MultiplayerService.js'; // Adjust path if necessary
import { P2P_MESSAGE_TYPES } from '../services/MultiplayerMessageTypes.js'; // Add import for message types

const SERVER_URL = '';

// Add this constant at the top of the file
const OWNER_USERNAME = 'deadlydog3000';

let discoveredPeers = {}; // Store announced peers: { peerId: { username, peerId, status } }

// Function to fetch recent chat messages
const fetchRecentMessages = async () => {
  try {
    const response = await fetch(`${SERVER_URL}/api/chat/recent`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include', // Include credentials if needed
    });

    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }

    const messages = await response.json();
    const sortedMessages = messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // Sort messages by timestamp
    const messagesContainer = document.getElementById('messages');
    messagesContainer.innerHTML = ''; // Clear existing messages
    sortedMessages.forEach(message => {
      // Pass the full message object to displayMessage for richer handling
      displayMessage(message);
    });
  } catch (err) {
    console.error('Error fetching recent messages:', err);
  }
};


// Function to initialize the chat
const initializeChat = () => {
  const pusher = new Pusher('429444f38162dce6c44b', {
    cluster: 'us3',
    encrypted: true
  });

  const user = getUser();
  if (!user || !user.username) {
    console.error('User not found or not logged in.');
    return;
  }

  // Track recently-sent text so the Pusher echo of our own message
  // doesn't render a duplicate (we already appended it optimistically).
  // key = `${type}|${message}`, value = timestamp ms. Entries expire
  // after 8s — well past Pusher's normal fan-out window.
  const pendingEchoes = new Map();
  const ECHO_DEDUPE_WINDOW_MS = 8000;
  function markPending(type, message) {
      pendingEchoes.set(`${type}|${message}`, Date.now());
  }
  function isOwnEcho(data) {
      if (!data || data.username !== user.username) return false;
      const key = `${data.type || 'normal'}|${data.message}`;
      const sentAt = pendingEchoes.get(key);
      if (!sentAt) return false;
      if (Date.now() - sentAt > ECHO_DEDUPE_WINDOW_MS) {
          pendingEchoes.delete(key);
          return false;
      }
      pendingEchoes.delete(key);
      return true;
  }

  const channel = pusher.subscribe('chat');
  channel.bind('message', function(data) { // data is the full message object from server
    console.log('Message received from Pusher:', data);

    // Lobby browser cares about HOST_ADVERTISEMENT / HOST_LEAVING but
    // doesn't want to poll the chat history. Fire a custom event so the
    // lobby UI (if open) refreshes within ~1s of the broadcast.
    if (data.type === 'host_advertisement' || data.type === 'host_leaving') {
        try {
            window.dispatchEvent(new CustomEvent('mp-hosts-changed', { detail: { type: data.type } }));
        } catch (_) {}
    }

    if (data.type === 'PEER_ANNOUNCEMENT') {
        try {
            // The actual payload is expected to be in data.message if sent via enhanced sendEventMessage
            const payload = typeof data.message === 'string' ? JSON.parse(data.message) : data.message;
            if (payload && payload.peerId) {
                handlePeerAnnouncement(payload);
            }
        } catch (e) {
            console.error('Error parsing PEER_ANNOUNCEMENT payload:', e, data.message);
        }
    } else if (isOwnEcho(data)) {
        // Suppress: we already rendered this optimistically.
    } else {
        displayMessage(data);
    }
  });

  const form = document.getElementById('chat-form');
  const input = document.getElementById('chat-input');

  const sendButton = form.querySelector('button');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.value) return;

    let messageData;
    // Admin chat commands. Only users with the OWNER badge can fire these;
    // anyone else typing /mute or /ban just sends the literal text. The
    // actions broadcast an announce-style chat message — enforcement
    // requires backend support (snordeluxe-server), so for now this is a
    // social-pressure tool rather than a hard mute / ban.
    const cmd = input.value.match(/^\/(mute|ban|unmute|unban)\s+(\S+)(?:\s+(.+))?$/i);
    if (cmd && isOwner(user.username)) {
        const action = cmd[1].toLowerCase();
        const targetUser = cmd[2];
        const reason = (cmd[3] || '').trim();
        const verb = action === 'mute' ? 'muted'
                   : action === 'ban' ? 'banned'
                   : action === 'unmute' ? 'unmuted'
                   : 'unbanned';
        const icon = (action === 'mute' || action === 'unmute') ? '🔇'
                   : (action === 'ban') ? '🚫'
                   : '✅';
        const tail = reason ? ` — ${reason}` : '';
        const text = `${icon} ${targetUser} has been ${verb} by ${user.username}${tail}`;
        messageData = { username: user.username, message: text, type: 'announce' };
    } else if (input.value.startsWith('/announce ')) {
      const announceMessage = input.value.slice(10).trim();
      messageData = { username: user.username, message: announceMessage, type: 'announce' };
    } else if (input.value.match(/^\/(mute|ban|unmute|unban)\s+/i)) {
      // Non-owner tried an admin command — surface a local-only hint and
      // don't broadcast anything. Easier than silently ignoring.
      input.value = '';
      displayMessage({ username: 'system', message: 'Only admins can use that command.', type: 'system' });
      input.focus();
      return;
    } else {
      messageData = { username: user.username, message: input.value, type: 'normal' };
    }

    // Optimistic UI: clear the input and render the message immediately.
    // Network call happens in the background; failures log to console.
    input.value = '';
    markPending(messageData.type, messageData.message);
    displayMessage(messageData);
    input.focus();

    fetch(`${SERVER_URL}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(messageData)
    }).catch(err => {
      console.error('Error sending message:', err);
    });
  };
  
  form.addEventListener('submit', handleSubmit);
  window.addEventListener('focus', fetchRecentMessages);

  // Fetch recent messages initially
  fetchRecentMessages();
  updateGameLobbyUI(); // Initial call to render the lobby (likely empty)

  // Cleanup event listeners when the chat is unmounted
  return () => {
    form.removeEventListener('submit', handleSubmit);
    window.removeEventListener('focus', fetchRecentMessages);
  };
};

const sendEventMessage = async (username, messageOrPayload, type = 'event') => {
  // If messageOrPayload is an object, stringify it for the 'message' field.
  // This keeps compatibility if the server expects 'message' to be a string,
  // and allows us to send structured data for our custom types like PEER_ANNOUNCEMENT.
  const messageContent = typeof messageOrPayload === 'object' ? JSON.stringify(messageOrPayload) : messageOrPayload;

  const messageData = {
    username,
    message: messageContent,
    type
  };

  try {
    await fetch(`${SERVER_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(messageData)
    });
  } catch (err) {
    console.error('Error sending event message:', err);
  }
};

function handlePeerAnnouncement(payload) {
    const localPeerId = getMyPeerId();
    const { peerId, username } = payload;

    if (!peerId || peerId === localPeerId) {
        return; // Don't connect to self or if peerId is missing
    }

    if (!discoveredPeers[peerId]) {
        console.log(`New peer announced: ${username} (${peerId})`);
        discoveredPeers[peerId] = { username, peerId, status: 'announced' };
    } else {
        // Update username if it changed, though peerId should be constant for a session
        discoveredPeers[peerId].username = username;
    }

    const activeConnections = getActiveConnections();
    if (!activeConnections[peerId] && discoveredPeers[peerId].status !== 'connecting') {
        console.log(`Attempting to connect to new peer: ${username} (${peerId})`);
        discoveredPeers[peerId].status = 'connecting';
        const conn = connectToPeer(peerId);
        if (conn) {
            conn.on('open', () => {
                console.log(`Successfully connected to ${username} (${peerId}) via P2P.`);
                discoveredPeers[peerId].status = 'connected';
                // Optionally send a direct greeting message via P2P
                // sendMessageToPeer(peerId, { type: 'GREETING', message: `Hello ${username}, I've connected!` });
                updateGameLobbyUI();
            });
            conn.on('close', () => {
                console.log(`P2P Connection closed with ${username} (${peerId}).`);
                if(discoveredPeers[peerId]) discoveredPeers[peerId].status = 'disconnected';
                updateGameLobbyUI();
            });
            conn.on('error', (err) => {
                console.error(`P2P Connection error with ${username} (${peerId}):`, err);
                if(discoveredPeers[peerId]) discoveredPeers[peerId].status = 'failed';
                updateGameLobbyUI();
            });
        } else {
            console.warn(`connectToPeer call for ${peerId} did not return a connection object.`);
            if(discoveredPeers[peerId]) discoveredPeers[peerId].status = 'failed';
        }
    }
    updateGameLobbyUI();
}

function updateGameLobbyUI() {
    const gameLobbyElement = document.getElementById('gameLobby');
    if (!gameLobbyElement) return;

    gameLobbyElement.innerHTML = '<h5>Connected Peers:</h5>';
    const list = document.createElement('ul');
    list.style.listStyleType = 'none';
    list.style.padding = '0';

    const localPeerId = getMyPeerId();
    const activeConns = getActiveConnections();

    if (Object.keys(discoveredPeers).length === 0 && localPeerId) {
        const noPeersItem = document.createElement('li');
        noPeersItem.textContent = 'Waiting for other players to join...';
        noPeersItem.style.fontSize = '11px';
        list.appendChild(noPeersItem);
    }

    for (const peerId in discoveredPeers) {
        if (peerId === localPeerId) continue; // Don't list self

        const peerInfo = discoveredPeers[peerId];
        const listItem = document.createElement('li');
        listItem.style.fontSize = '11px';
        listItem.style.marginBottom = '3px';
        
        let statusText = peerInfo.status;
        if (activeConns[peerId] && activeConns[peerId].open) {
            statusText = 'Connected (P2P)';
             if(discoveredPeers[peerId]) discoveredPeers[peerId].status = 'connected'; // Ensure status is updated
        } else if (peerInfo.status === 'connecting') {
            statusText = 'Connecting...';
        }

        listItem.textContent = `${peerInfo.username || 'Unknown User'} (${peerId.substring(0,8)}...): ${statusText}`;
        list.appendChild(listItem);
    }
    gameLobbyElement.appendChild(list);
}

const displayMessage = (messageData) => { // Expects the full message object
  const messages = document.getElementById('messages');
  const { username, message, type = 'normal' } = messageData; // Destructure

  // Do not display PEER_ANNOUNCEMENT as regular chat messages
  if (type === 'PEER_ANNOUNCEMENT') {
      return;
  }

  // Hide system messages where content is entirely contained in curly braces
  if (message && typeof message === 'string' && message.trim().match(/^{.*}$/)) {
      return;
  }

  if (type === 'announce') {
    // Create a separate chiron message bubble
    const chironItem = document.createElement('li');
    chironItem.classList.add('announce-chiron');
    chironItem.innerHTML = `<span>🔥 ANNOUNCEMENT 🔥</span>`;
    messages.appendChild(chironItem);
  }

  const item = document.createElement('li');
  switch (type) {
    case 'battle':
      item.classList.add('battle-message');
      break;
    case 'store':
      item.classList.add('store-message');
      break;
    case 'training':
      item.classList.add('training-message');
      break;
    case 'announce':
      item.classList.add('announce-message');
      break;
    default:
      item.classList.add('normal-message');
  }

  item.innerHTML = `<strong>${username}</strong>${ownerBadgeHTML(username)}: ${message}`;
  messages.appendChild(item);
  console.log('Appending message to messages list');
  messages.scrollTop = messages.scrollHeight; // Scroll to the latest message
};

// Returns ALL active host advertisements (deduped by hostPeerId,
// keeping the most recent ad per host, and filtering out hosts that
// have since announced HOST_LEAVING). The lobby browser calls this on
// open and on every `mp-hosts-changed` event.
async function findActiveHostsInChat() {
    // Must be > HOST_ADVERTISEMENT_INTERVAL (15s in battle-manager.js) so
    // a healthy host's most recent ad always passes the freshness check,
    // but small enough that ungraceful disconnects don't linger as phantom
    // hosts that fail to connect when joined.
    const MAX_HOST_AGE_MS = 30000;
    const RECENT_HOST_AGE_MS = 15000;
    try {
        const response = await fetch(`${SERVER_URL}/api/chat/recent`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'include',
        });
        if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
        const messages = await response.json();
        const sortedMessages = messages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        const leftHosts = new Set();
        for (const message of sortedMessages) {
            if (message.type === P2P_MESSAGE_TYPES.HOST_LEAVING) {
                try {
                    let payload = message.message;
                    if (typeof payload === 'string') payload = JSON.parse(payload);
                    if (payload && payload.hostPeerId) leftHosts.add(payload.hostPeerId);
                } catch (e) { /* ignore */ }
            }
        }

        const seen = new Set();
        const hosts = [];
        for (const message of sortedMessages) {
            if (message.type !== P2P_MESSAGE_TYPES.HOST_ADVERTISEMENT) continue;
            try {
                let payload = message.message;
                if (typeof payload === 'string') payload = JSON.parse(payload);
                if (!payload || !payload.hostPeerId) continue;
                if (leftHosts.has(payload.hostPeerId)) continue;
                if (seen.has(payload.hostPeerId)) continue;
                if (!payload.hostUsername) continue;
                // Use the server's stored timestamp, not the payload one — the
                // payload timestamp is the host device's local clock, which can
                // drift across devices and falsely age out (or rejuvenate) ads.
                const serverTs = new Date(message.timestamp).getTime();
                const adTimestamp = Number.isFinite(serverTs) ? serverTs : (payload.timestamp || 0);
                const ageMs = Date.now() - adTimestamp;
                if (ageMs > MAX_HOST_AGE_MS) continue;
                payload.isRecent = ageMs <= RECENT_HOST_AGE_MS;
                payload.ageMs = ageMs;
                seen.add(payload.hostPeerId);
                hosts.push(payload);
            } catch (e) { /* skip malformed */ }
        }
        return hosts;
    } catch (err) {
        console.error('[Chat] findActiveHostsInChat error:', err);
        return [];
    }
}

// Back-compat: the old `findActiveHostInChat` returned the single most
// recent active host. Keep it as a wrapper so any caller that hasn't
// been updated still works.
async function findActiveHostInChat() {
    const hosts = await findActiveHostsInChat();
    return hosts.length ? hosts[0] : null;
}

// Old enhanced finder retained for legacy callers but rerouted.
async function _legacy_findActiveHostInChat() {
    console.log("[Chat] Attempting to find active host in chat history...");
    const MAX_HOST_AGE_MS = 90000; // Consider hosts stale after 90 seconds (2x the update interval)
    const RECENT_HOST_AGE_MS = 30000; // Consider hosts "recent" if updated within 30 seconds
    
    try {
        const response = await fetch(`${SERVER_URL}/api/chat/recent`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const messages = await response.json();
        const sortedMessages = messages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Sort by newest first

        // First, collect all hosts that have left
        const leftHosts = new Set();
        for (const message of sortedMessages) {
            if (message.type === P2P_MESSAGE_TYPES.HOST_LEAVING) {
                try {
                    let leavingPayload = message.message;
                    if (typeof leavingPayload === 'string') {
                        leavingPayload = JSON.parse(leavingPayload);
                    }
                    if (leavingPayload && leavingPayload.hostPeerId) {
                        leftHosts.add(leavingPayload.hostPeerId);
                        console.log(`[Chat] Host ${leavingPayload.hostPeerId} has announced they're leaving`);
                    }
                } catch (e) {
                    console.error("[Chat] Error parsing HOST_LEAVING message:", e);
                }
            }
        }

        // Now find active hosts, excluding those that have left
        for (const message of sortedMessages) {
            if (message.type === P2P_MESSAGE_TYPES.HOST_ADVERTISEMENT) {
                try {
                    let hostAdPayload = message.message;
                    if (typeof hostAdPayload === 'string') {
                        hostAdPayload = JSON.parse(hostAdPayload);
                    }

                    if (hostAdPayload && hostAdPayload.hostPeerId) {
                        // Skip if this host has announced they're leaving
                        if (leftHosts.has(hostAdPayload.hostPeerId)) {
                            console.log(`[Chat] Skipping host ${hostAdPayload.hostPeerId} - they've announced leaving`);
                            continue;
                        }

                        // Basic validation: ensure essential fields are present
                        if (hostAdPayload.hostUsername && hostAdPayload.snordeluxeName && hostAdPayload.gameName) {
                            // Check if the advertisement is fresh
                            const adTimestamp = hostAdPayload.timestamp || new Date(message.timestamp).getTime();
                            const ageMs = Date.now() - adTimestamp;
                            
                            if (ageMs > MAX_HOST_AGE_MS) {
                                console.log(`[Chat] Found HOST_ADVERTISEMENT but it's stale (${Math.round(ageMs/1000)}s old):`, hostAdPayload);
                                continue; // Skip stale advertisements
                            }
                            
                            // Add a flag to indicate if this is a "recent" host
                            hostAdPayload.isRecent = ageMs <= RECENT_HOST_AGE_MS;
                            hostAdPayload.ageMs = ageMs;
                            
                            console.log(`[Chat] Found active host advertisement (${Math.round(ageMs/1000)}s old, recent=${hostAdPayload.isRecent}):`, hostAdPayload);
                            return hostAdPayload;
                        } else {
                            console.warn("[Chat] Found HOST_ADVERTISEMENT but it's missing some fields:", hostAdPayload);
                        }
                    }
                } catch (e) {
                    console.error("[Chat] Error parsing HOST_ADVERTISEMENT message content:", e, message.message);
                }
            }
        }
        console.log("[Chat] No active HOST_ADVERTISEMENT found in recent messages.");
        return null;
    } catch (err) {
        console.error('[Chat] Error in findActiveHostInChat:', err);
        return null;
    }
}

export { initializeChat, sendEventMessage, findActiveHostInChat, findActiveHostsInChat };
